"use client";

import { useEffect, useMemo, useState } from "react";
import CourseSectionVideos from "./CourseSectionVideos";
import { fetchCourseLearnerState } from "../lib/courseLearnerApi";
import { claimCertificate, fetchCourseSections } from "../lib/courseSectionsApi";
import { loadCourseQuizProgress } from "../lib/courseQuizProgress";
import styles from "../[id]/course-details.module.scss";

/**
 * Section gating uses the Courses API learner-state when logged in (quiz pass + enrollment).
 * Guests fall back to localStorage progress.
 *
 * @param {{
 *   courseId: string | number,
 *   sections: { key: string, title: string, episodes: unknown[], quizId: number | null, reading?: string }[],
 *   videoStyle?: unknown,
 *   accordionId?: string,
 * }} props
 */
export default function CourseContentAccordion({
    courseId,
    sections,
    videoStyle,
    accordionId = "courseAccordion",
}) {
    const [progressRev, setProgressRev] = useState(0);
    const [learner, setLearner] = useState(null);
    // New: server-side per-section question-gating state + running course score.
    const [sectionsState, setSectionsState] = useState(null);

    const quizIds = useMemo(
        () =>
            sections
                .map((s) => s.quizId)
                .filter((id) => typeof id === "number" && id > 0),
        [sections]
    );

    useEffect(() => {
        let cancelled = false;

        const load = () => {
            void fetchCourseLearnerState(courseId, quizIds).then((data) => {
                if (!cancelled) setLearner(data);
            });
            void fetchCourseSections(courseId).then((data) => {
                if (!cancelled) setSectionsState(data);
            });
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [courseId, quizIds, progressRev]);

    /** Gated sections (configured with specific questions) keyed by section key. */
    const gatedByKey = useMemo(() => {
        const map = {};
        for (const s of sectionsState?.sections || []) {
            map[s.key] = s;
        }
        return map;
    }, [sectionsState]);

    useEffect(() => {
        const onReload = () => setProgressRev((n) => n + 1);
        if (typeof window !== "undefined") {
            window.addEventListener("figmawya-course-learner-reload", onReload);
            return () => window.removeEventListener("figmawya-course-learner-reload", onReload);
        }
        return undefined;
    }, []);

    const progress = useMemo(() => {
        const local = loadCourseQuizProgress(courseId);
        const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
        if (!token || learner === null) {
            return local;
        }
        const qp = learner.quizPasses || {};
        const map = {};
        for (const s of sections) {
            if (!s.quizId) continue;
            if (qp[String(s.quizId)]?.passed) {
                map[s.key] = { passed: true, quizId: s.quizId };
            }
        }
        return map;
    }, [courseId, sections, learner, progressRev]);

    const bumpProgress = () => setProgressRev((n) => n + 1);

    const [claiming, setClaiming] = useState(false);
    const [claimError, setClaimError] = useState("");

    /** "طلب شهادتي" — manual fallback; the same issuance also happens automatically
     *  server-side the moment the course is completed via a section submission. */
    const handleClaimCertificate = async () => {
        if (claiming) return;
        setClaiming(true);
        setClaimError("");
        try {
            const res = await claimCertificate(courseId);
            if (!res.ok) {
                setClaimError(res.message || "تعذر إصدار الشهادة، حاول مرة أخرى.");
                return;
            }
            bumpProgress(); // refreshes sectionsState.certificate -> switches button to "تحميل"
        } finally {
            setClaiming(false);
        }
    };

    const globalLock = learner !== null && learner.canViewContent === false;
    const enrollmentAccess = learner?.enrollmentAccess ?? "none";

    /** When enrollment or login blocks content, ignore local quiz progress for badges / chain. */
    const effectiveProgress = globalLock ? {} : progress;

    /**
     * There is one quiz per course (related_quiz); section.quizId (from getCourseSections)
     * is already that quiz id whenever the section has q_nums set, else null. Fall back to
     * the gating endpoint's quizId only as a defensive extra (e.g. stale ACF cache).
     */
    const effectiveQuizId = (section) =>
        section?.quizId ?? gatedByKey[section?.key]?.quizId ?? null;

    /** Unified pass state: gated sections use the server's question-gating result; others use whole-quiz progress. */
    const sectionPassed = (section) => {
        if (!effectiveQuizId(section)) return false;
        if (gatedByKey[section.key]) {
            return globalLock ? false : Boolean(gatedByKey[section.key].passed);
        }
        return Boolean(effectiveProgress[section.key]?.passed);
    };

    const isBodyLocked = (index) => {
        if (globalLock) return true;
        if (index === 0) return false;
        const prev = sections[index - 1];
        if (!effectiveQuizId(prev)) return false;
        return !sectionPassed(prev);
    };

    const hasCourseScore =
        sectionsState && Array.isArray(sectionsState.sections) && sectionsState.sections.length > 0;

    return (
        <div className="course-content-accordion">
            {enrollmentAccess === "login_required" ? (
                <div className="alert alert-warning border-0 shadow-sm mb-3" role="status">
                    يتطلب هذه الدورة موافقة على الانضمام. استخدم نموذج الطلب أعلى الصفحة أو{" "}
                    <a href="/login">سجّل الدخول</a>.
                </div>
            ) : null}
            {enrollmentAccess === "pending" ? (
                <div className="alert alert-info border-0 shadow-sm mb-3" role="status">
                    طلب الانضمام قيد المراجعة. بعد الموافقة ستُفتح لك الدروس والاختبارات.
                </div>
            ) : null}
            {enrollmentAccess === "refused" ? (
                <div className="alert alert-secondary border-0 shadow-sm mb-3" role="status">
                    تم رفض طلب الانضمام لهذه الدورة. للاستفسار تواصل مع الدعم.
                </div>
            ) : null}

            {hasCourseScore && !globalLock ? (
                <div
                    className={`d-flex align-items-center justify-content-between flex-wrap gap-2 rounded-4 border p-3 mb-3 ${
                        sectionsState.courseCompleted ? "bg-success-subtle border-success" : "bg-light"
                    }`}
                    dir="rtl"
                    role="status"
                >
                    <div className="fw-bold">
                        <i className="bi bi-mortarboard-fill me-2" aria-hidden />
                        درجتك في الدورة: {Math.round(sectionsState.courseScore)} / {Math.round(sectionsState.configuredMaxScore)}
                        <span className="text-muted ms-2">({Math.round(sectionsState.coursePercentage)}%)</span>
                    </div>
                    {sectionsState.courseCompleted ? (
                        <span className="badge bg-success px-3 py-2">
                            <i className="bi bi-trophy-fill me-1" aria-hidden />
                            أكملت الدورة 🎉
                        </span>
                    ) : null}
                </div>
            ) : null}

            {sectionsState?.certificate && !globalLock ? (
                <div className="d-flex flex-column gap-2 mb-3" dir="rtl">
                    {sectionsState.certificate.hasCert ? (
                        <a
                            href={sectionsState.certificate.certVerifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-success rounded-pill fw-bold"
                        >
                            <i className="bi bi-award-fill me-2" aria-hidden />
                            قم بتحميل شهادتي
                        </a>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-outline-success rounded-pill fw-bold"
                            disabled={claiming || !sectionsState.courseCompleted}
                            title={
                                !sectionsState.courseCompleted
                                    ? "أكمل جميع أقسام الدورة أولاً للحصول على الشهادة"
                                    : undefined
                            }
                            onClick={handleClaimCertificate}
                        >
                            <i className="bi bi-award me-2" aria-hidden />
                            {claiming ? "جاري الإصدار..." : "طلب شهادتي"}
                        </button>
                    )}
                    {claimError ? <div className="text-danger small">{claimError}</div> : null}
                </div>
            ) : null}

            <div className="accordion" id={accordionId}>
                {sections.map((section, index) => {
                    const headingId = `heading-${section.key}`;
                    const collapseId = `collapse-${section.key}`;
                    const videoCount = section.episodes?.length ?? 0;
                    const locked = isBodyLocked(index);
                    const passed = sectionPassed(section);
                    const quizId = effectiveQuizId(section);

                    const badgeText =
                        videoCount > 0 ? `${videoCount} فيديو` : quizId ? "اختبار" : "";

                    return (
                        <div className="accordion-item" key={section.key}>
                            <h2 className="accordion-header" id={headingId}>
                                <button
                                    className={`accordion-button ${styles.accordionBtn} ${
                                        index !== 0 ? "collapsed" : ""
                                    }`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#${collapseId}`}
                                >
                                    <span
                                        className="badge rounded-pill bg-light text-dark border me-2 flex-shrink-0"
                                        style={{ minWidth: 24 }}
                                    >
                                        {index + 1}
                                    </span>
                                    <span className="text-truncate me-2 flex-shrink-1 min-w-0">{section.title}</span>
                                    {passed && quizId ? (
                                        <span className="badge bg-success ms-1 border">
                                            <i className="bi bi-check-circle-fill me-1" aria-hidden />
                                            نجح
                                        </span>
                                    ) : null}
                                    {badgeText ? (
                                        <span className="badge bg-light text-dark ms-auto me-2 border">
                                            {badgeText}
                                        </span>
                                    ) : null}
                                </button>
                            </h2>
                            <div
                                id={collapseId}
                                className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                                data-bs-parent={`#${accordionId}`}
                            >
                                <div className="accordion-body">
                                    <CourseSectionVideos
                                        episodes={section.episodes}
                                        quizId={quizId}
                                        videoStyle={videoStyle}
                                        courseId={courseId}
                                        sectionKey={section.key}
                                        sectionTitle={section.title}
                                        contentLocked={locked}
                                        sectionQuizPassed={passed}
                                        gated={Boolean(gatedByKey[section.key])}
                                        sectionThreshold={gatedByKey[section.key]?.threshold}
                                        reading={section.reading}
                                        readingConfirmed={Boolean(gatedByKey[section.key]?.readingConfirmed)}
                                        onProgressChange={bumpProgress}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
